/**
 * Routing and the load/auth gate.
 *
 * Every screen needs the whole snapshot, so it is fetched once here and handed
 * down as a derived `Budget`. That keeps loading, empty, and error states in one
 * place instead of repeated on five pages.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { periodContaining, type PaycheckPeriod, type Snapshot } from '@alohamula/domain';
import { AppShell } from './components/AppShell';
import { ErrorState, Skeleton } from './components/ui';
import { useAuth } from './auth/AuthProvider';
import { SignInPage } from './auth/SignInPage';
import { setTokenSource } from './api/client';
import { refreshSnapshot, useSnapshot, presenceKey } from './api/queries';
import { useBudget, type Budget } from './budget/useBudget';
import { useRealtime, type RealtimeStatus } from './realtime/useRealtime';
import { OverviewPage } from './pages/OverviewPage';
import { SortPage } from './pages/SortPage';
import { BudgetItemsPage } from './pages/BudgetItemsPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ChatPage } from './pages/ChatPage';
import { MapPage } from './pages/MapPage';
import { NotFoundPage } from './pages/NotFoundPage';

export function App() {
  const { state, getAccessToken, signOut } = useAuth();

  // The API client is a module rather than a hook, so its token source is wired
  // in once here. Effect, not render, because it is a side effect on a module.
  useEffect(() => {
    setTokenSource(getAccessToken);
  }, [getAccessToken]);

  if (state.status === 'restoring') return <FullPageLoading />;
  if (state.status === 'signedOut') return <SignInPage />;

  return (
    <SignedInApp
      email={state.session.email}
      onSignOut={signOut}
      getAccessToken={getAccessToken}
    />
  );
}

function SignedInApp({
  email,
  onSignOut,
  getAccessToken,
}: {
  email: string;
  onSignOut: () => void;
  getAccessToken: () => Promise<string | null>;
}) {
  const queryClient = useQueryClient();
  const snapshotQuery = useSnapshot();

  const onChanged = useCallback(() => {
    void refreshSnapshot(queryClient);
  }, [queryClient]);

  const onPresenceChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: presenceKey });
  }, [queryClient]);

  const realtimeStatus = useRealtime({
    getAccessToken,
    onChanged,
    onPresenceChanged,
    enabled: true,
  });

  if (snapshotQuery.isPending) return <FullPageLoading />;

  if (snapshotQuery.isError) {
    return (
      <ErrorState
        title="Could not load your budget"
        body={
          snapshotQuery.error instanceof Error
            ? snapshotQuery.error.message
            : 'Something went wrong. Try again.'
        }
        onRetry={() => void snapshotQuery.refetch()}
      />
    );
  }

  return (
    <BudgetApp
      snapshot={snapshotQuery.data}
      email={email}
      onSignOut={onSignOut}
      realtimeStatus={realtimeStatus}
      isRefreshing={snapshotQuery.isFetching}
      onRefresh={onChanged}
    />
  );
}

function BudgetApp({
  snapshot,
  email,
  onSignOut,
  realtimeStatus,
  isRefreshing,
  onRefresh,
}: {
  snapshot: Snapshot;
  email: string;
  onSignOut: () => void;
  realtimeStatus: RealtimeStatus;
  isRefreshing: boolean;
  onRefresh: () => void;
}) {
  // Which paycheck period the screens show. Defaults to the current one; the
  // overview steps back through earlier ones.
  const [period, setPeriod] = useState<PaycheckPeriod>(() => periodContaining());

  /*
   * "Now" is pinned to the server's clock for the life of a snapshot. Using
   * `new Date()` inside the derivation would make `useBudget`'s memo recompute on
   * every render, and would let a stale hold's age tick over mid-interaction.
   */
  const now = useMemo(() => new Date(snapshot.serverTime), [snapshot.serverTime]);

  const budget = useBudget(snapshot, period, now);

  return (
    <Routes>
      <Route
        element={
          <AppShell
            toSortCount={budget.uncategorized.length}
            realtimeStatus={realtimeStatus}
            email={email}
            onSignOut={onSignOut}
          />
        }
      >
        <Route
          index
          element={
            <OverviewPage
              budget={budget}
              onSelectPeriod={setPeriod}
              isRefreshing={isRefreshing}
              onRefresh={onRefresh}
            />
          }
        />
        <Route path="sort" element={<SortPage budget={budget} now={now} />} />
        <Route path="plan" element={<BudgetItemsPage budget={budget} />} />
        <Route path="envelopes" element={<BudgetItemsPage budget={budget} />} />
        <Route path="bills" element={<BudgetItemsPage budget={budget} />} />
        <Route path="transactions" element={<TransactionsPage budget={budget} now={now} />} />
        <Route path="chat" element={<ChatPage budget={budget} />} />
        <Route path="map" element={<MapPage budget={budget} now={now} />} />
        <Route path="settings" element={<SettingsPage budget={budget} now={now} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/**
 * A skeleton rather than a spinner: it shows the shape of what is coming, which
 * makes the wait feel shorter and stops the layout jumping when data lands.
 */
function FullPageLoading() {
  return (
    <div
      style={{
        maxWidth: 'var(--layout-max-width)',
        margin: '0 auto',
        padding: 'var(--space-lg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-lg)',
      }}
    >
      <span className="sr-only" role="status">
        Loading your budget
      </span>
      <Skeleton height={28} width={220} />
      <Skeleton height={132} />
      <div style={{ display: 'grid', gap: 'var(--space-sm)', gridTemplateColumns: '1fr 1fr' }}>
        <Skeleton height={96} />
        <Skeleton height={96} />
      </div>
      <Skeleton height={220} />
    </div>
  );
}

export type { Budget };
